import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import products from '../../data/products';

const navLink = ({ isActive }) =>
  `text-sm font-medium transition-colors ${isActive ? 'text-bright' : 'text-slate hover:text-bright'}`;

export default function MarketingNav() {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setProductsOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-hairline bg-paper/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-site items-center justify-between px-6 md:px-10">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo/edai-mark-dark-transparent.svg" alt="" aria-hidden="true" className="h-6 w-auto" />
          <span className="font-serif text-xl tracking-tight">EdAI Systems</span>
          <span aria-hidden="true" className="hidden h-2 w-2 rounded-[1px] bg-seal sm:inline-block" />
          <span className="hidden text-sm text-slate sm:inline">The Standard of Knowledge</span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setProductsOpen((v) => !v)}
              aria-expanded={productsOpen}
              aria-haspopup="true"
              className={`text-sm font-medium transition-colors ${
                location.pathname.startsWith('/products') ? 'text-bright' : 'text-slate hover:text-bright'
              }`}
            >
              Products
            </button>
            {productsOpen && (
              <div className="absolute left-0 top-full mt-3 w-72 rounded border border-hairline bg-surface p-2 shadow-sm">
                {products.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/products/${p.slug}`}
                    className="block rounded px-3 py-2 transition-colors hover:bg-white/5"
                  >
                    <span className="block text-sm font-semibold text-bright">{p.name}</span>
                    <span className="block text-xs text-slate">{p.audienceTag}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <NavLink to="/who-its-for" className={navLink}>
            Who it’s for
          </NavLink>
          <NavLink to="/about" className={navLink}>
            About
          </NavLink>
          <NavLink to="/blog" className={navLink}>
            Writing
          </NavLink>
          <Link to="/contact" className="rounded bg-seal px-4 py-2 text-sm font-semibold text-ink-deep transition-all hover:brightness-110">
            Request a demo
          </Link>
        </nav>

        <button
          type="button"
          className="lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
            {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>
      </div>

      {open && (
        <nav className="border-t border-hairline bg-paper px-6 py-4 lg:hidden" aria-label="Mobile">
          <p className="pb-1 text-xs text-faint">Products</p>
          {products.map((p) => (
            <Link key={p.slug} to={`/products/${p.slug}`} className="block py-2 text-sm font-medium text-bright">
              {p.name}
              <span className="ml-2 text-xs font-normal text-slate">{p.audienceTag}</span>
            </Link>
          ))}
          <div className="mt-3 border-t border-hairline pt-3">
            <Link to="/who-its-for" className="block py-2 text-sm font-medium text-bright">
              Who it’s for
            </Link>
            <Link to="/about" className="block py-2 text-sm font-medium text-bright">
              About
            </Link>
            <Link to="/blog" className="block py-2 text-sm font-medium text-bright">
              Writing
            </Link>
            <Link to="/contact" className="mt-3 inline-block rounded bg-seal px-4 py-2 text-sm font-semibold text-ink-deep">
              Request a demo
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
